import type { User } from '../contexts/AuthContext';

/**
 * Get the display name for a user.
 * Priority: firstName + lastName → username → email → 'User'
 *
 * Note: businessName is intentionally NOT used as a fallback, following
 * the existing codebase pattern where personal names are preferred for
 * display purposes.
 *
 * @param user - The user object from AuthContext
 * @returns The user's display name
 */
export const getUserDisplayName = (user: User | null): string => {
  if (!user) return 'User';

  // Priority 1: Full name (firstName + lastName)
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  // Priority 2: Username (Cognito username)
  if (user.username) {
    return user.username;
  }

  // Priority 3: Email
  if (user.email) {
    return user.email;
  }

  // Fallback: Generic "User"
  return 'User';
};

/**
 * Get the artist display name for artwork attribution.
 * Returns the displayName if set, otherwise returns empty string.
 *
 * Returning empty string (not 'anon') prevents the UI from flashing
 * a placeholder value before the real name loads (issue #966).
 *
 * @param displayName - The user's display name from their profile (may be null/undefined)
 * @returns The display name or empty string as fallback
 */
export const getArtistDisplayName = (displayName?: string | null): string => {
  // Trim whitespace if displayName exists
  const trimmed = displayName?.trim();

  // Return displayName if set and non-empty, otherwise empty string
  if (trimmed && trimmed.length > 0) {
    return trimmed;
  }

  return '';
};
